import {Loop} from '../../index.js';

/**
 * Creates Hypercube.js Game Engines.
 * @example
 * const engine = new Engine();
 */
class Engine {

    /**
     * Stores the viewer device.
     * @type {import('../../index.js').DeviceViewer}
     * @private
     */
    $deviceviewer;

    /**
     * Stores the loop.
     * @type {import('../../index.js').Loop}
     * @private
     */
    $loop;

    /**
     * Stores current scene.
     * @type {import('../../index.js').Scene}
     * @private
     */
    $scene;

    /**
     * Stores the systems.
     * @type {import('../../index.js').System[]}
     * @private
     */
    $systems;

    /**
     * Gets the viewer device.
     * @type {import('../../index.js').DeviceViewer}
     * @public
     * @readonly
     */
    get deviceviewer() {

        return this.$deviceviewer;
    }

    /**
     * Gets current scene.
     * @type {import('../../index.js').Scene}
     * @public
     * @readonly
     */
    get scene() {

        return this.$scene;
    }

    /**
     * Creates a new Hypercube.js Game Engine.
     */
    constructor() {

        this.$systems = [];
    }

    /**
     * Destroys the engine.
     * @public
     */
    destroy() {

        if (typeof this.$loop !== 'undefined') {

            this.$loop.destroy();
        }

        this.$systems.forEach(($system) => {

            $system.destroy();
        });
    }

    /**
     * Resizes the game once.
     * @public
     */
    resize() {

        if (typeof this.$deviceviewer !== 'undefined') {

            this.$deviceviewer.setsize();
        }

        this.$systems.forEach(($system) => {

            $system.resize();
        });
    }

    /**
     * Runs the loop.
     * @param {number} [$framerate] The framerate (in fps) to run the loop.
     * @public
     */
    run($framerate = 60) {

        if (typeof this.$loop !== 'undefined') {

            this.$loop.destroy();
        }

        this.$loop = new Loop(this.tick.bind(this));

        this.$loop.run($framerate);
    }

    /**
     * Sets the viewer device.
     * @param {import('../../index.js').DeviceViewer} $deviceviewer The viewer device to set.
     * @public
     */
    setdeviceviewer($deviceviewer) {

        this.$deviceviewer = $deviceviewer;
    }

    /**
     * Starts each system.
     * @public
     */
    start() {

        this.$systems.forEach(($system) => {

            $system.start();
            $system.tick(0);
        });
    }

    /**
     * Updates each system once.
     * @param {number} $timeframe The timeframe duration (in ms).
     * @public
     */
    tick($timeframe) {

        this.$systems.forEach(($system) => {

            $system.tick($timeframe);
        });
    }

    /**
     * Uses systems with given scene.
     * @param {import('../../index.js').Scene} $scene The scene to use.
     * @param {import('../../index.js').System[]} $systems The systems to use.
     * @public
     */
    use($scene, $systems) {

        const systems = [...$systems];

        this.$scene = $scene;
        this.$systems = systems;

        this.$systems.forEach(($system) => {

            $system.setengine(this);
        });
    }
}

export {

    Engine
};

export default Engine;
